'use client';
import React, { useRef, useState } from 'react';
import WhereToBuyModal from '@/app/components/WhereToBuyModal';
import AppImage from '@/components/ui/AppImage';
import { fireFloodlightPixel, FLOODLIGHT_ACTIVITIES } from '@/lib/floodlight';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  platformLogo: string;
  platformLogoAlt: string;
  rating: number;
  product: string;
  quote: string;
  impact: string;
  tag: string;
}

const testimonials: Testimonial[] = [
{
  id: 1,
  name: 'Avipsha Deota',
  location: 'Bolangir',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Chhatua',
  quote: 'Being from Odisha, it feels so good to have these products. It\'s been 2–3 months and I\'ve continuously ordered eight times — Odisha is grateful to have such products.',
  impact: 'Odisha-proud repeat customer — ordered 8 times in 2–3 months.',
  tag: 'Repeat Buyer'
},
{
  id: 2,
  name: 'Mausumi Das',
  location: 'Mehsana',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Chana Sattu',
  quote: 'Very tasty Sattu. Already loving it. Giving reviews after one week.',
  impact: 'Still loving it a week after the first order.',
  tag: 'Health Impact'
},
{
  id: 3,
  name: 'Debasish Swain',
  location: 'Nilagiri',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Chhatua',
  quote: 'A lovely taste and good for health. I\'m from the north-east part of Odisha — it\'s not available in the local market, so I had to order online.',
  impact: 'Ordered online since it isn\'t available in local markets back home.',
  tag: 'Home Away From Home'
},
{
  id: 4,
  name: 'Sudeepta Kumar Sahu',
  location: 'Raipur',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Chhatua',
  quote: 'Good quality chhatua. Highly recommended for bachelors living away from home.',
  impact: 'A go-to breakfast for bachelors living away from home.',
  tag: 'Home Away From Home'
},
{
  id: 5,
  name: 'Gyanranjan Sahoo',
  location: 'Mysore',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Chocolate Chhatua',
  quote: 'The item was very good with a yummy taste, especially for kids — thanks, Kasturi Chhatua and Flipkart.',
  impact: 'A hit with the kids at breakfast.',
  tag: 'Kids Favourite'
},
{
  id: 6,
  name: 'Rajat Bal',
  location: 'Hyderabad',
  platformLogo: '/assets/images/flipkart-com-logo.png',
  platformLogoAlt: 'Verified review on Flipkart',
  rating: 5,
  product: 'Mix Chhatua',
  quote: 'Taste is good and the ingredients are great — low in sugar too.',
  impact: 'Praised for great ingredients and low sugar.',
  tag: 'Health Impact'
},
{
  id: 8,
  name: 'Prasanta Mishra',
  location: 'Verified Buyer',
  platformLogo: '/assets/images/Amazon_icon.png',
  platformLogoAlt: 'Verified review on Amazon',
  rating: 5,
  product: 'Chhatua',
  quote: 'Healthy breakfast option for years. Quality is top-notch. Absolute favourite of my family.',
  impact: 'A trusted family breakfast for years.',
  tag: 'Family Win'
},
{
  id: 9,
  name: 'Vishal',
  location: 'Verified Buyer',
  platformLogo: '/assets/images/Amazon_icon.png',
  platformLogoAlt: 'Verified review on Amazon',
  rating: 5,
  product: 'Chhatua',
  quote: 'It tastes so good. Quick meal and easy to prepare. I always carry it while I travel abroad.',
  impact: 'Packs it along on every trip abroad.',
  tag: 'On-the-Go'
},
{
  id: 10,
  name: 'Bipasha Pradhan',
  location: 'Verified Buyer',
  platformLogo: '/assets/images/Amazon_icon.png',
  platformLogoAlt: 'Verified review on Amazon',
  rating: 5,
  product: 'Chhatua',
  quote: 'Bought it for my mother in Mumbai because we\'re from Odisha and it isn\'t available there. Happy with the product.',
  impact: 'Brought a taste of home to family living outside Odisha.',
  tag: 'Family Win'
}];


function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) =>
      <svg
        key={i}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill={i < rating ? 'var(--primary)' : 'none'}
        stroke={i < rating ? 'var(--primary)' : 'var(--muted-foreground)'}
        strokeWidth="1.5">

          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )}
    </div>);

}

export default function TestimonialsSection() {
  const [buyOpen, setBuyOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section id="testimonials" className="py-20 md:py-28 overflow-hidden" style={{ background: 'var(--secondary)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="mb-14 md:mb-18 reveal">
          <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent-ink)' }}>
            Real Stories
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="font-display leading-none"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.6rem)', fontWeight: 200, letterSpacing: '0.02em', color: 'var(--foreground)' }}>

              Families who{' '}
              <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>trust</span>
              <br />Kasturi every day.
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs md:text-right">
              Not influencer posts. Not paid reviews.<br />
              Real people, real kitchens, real results.
            </p>
          </div>
        </div>

        {/* Mobile: swipeable carousel. Tablet/desktop: even grid rows */}
        <p className="md:hidden text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Swipe for more →
        </p>
        <div
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 pb-2 gap-4 md:mx-0 md:px-0 md:pb-0 md:grid md:grid-cols-2 md:gap-5 lg:grid-cols-3 md:overflow-visible md:items-stretch"
          ref={scrollRef}>
          {testimonials.map((t, i) =>
          <div
            key={t.id}
            className={`flex flex-col rounded-2xl p-6 reveal stagger-${i % 5 + 1} shrink-0 w-[82%] sm:w-[60%] snap-center md:w-auto md:shrink`}
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>

              {/* Header row */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 p-1"
                  style={{ background: '#FFFFFF', border: '1.5px solid var(--border)' }}>

                    <AppImage
                    src={t.platformLogo}
                    alt={t.platformLogoAlt}
                    width={32}
                    height={32}
                    className="w-full h-full object-contain rounded-full" />

                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{t.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{t.location}</p>
                  </div>
                </div>
                <span
                className="text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full font-semibold shrink-0"
                style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>

                  {t.tag}
                </span>
              </div>

              {/* Rating + product */}
              <div className="flex items-center gap-2 mb-3">
                <StarRating rating={t.rating} />
                <span className="text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>· {t.product}</span>
              </div>

              {/* Quote */}
              <blockquote className="text-sm leading-relaxed mb-4 flex-1" style={{ color: 'var(--foreground)' }}>
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {/* Impact */}
              <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-xl mt-auto"
              style={{ background: 'var(--secondary)' }}>

                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-ink)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <p className="text-[11px] leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{t.impact}</p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center reveal">
          <p className="text-muted-foreground text-sm mb-5">
            Join the families who&apos;ve made the switch to real, clean nutrition.
          </p>
          <button
            type="button"
            onClick={() => { fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.homeShopButton); setBuyOpen(true); }}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:opacity-90 hover:gap-4"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>

            Shop @ Kasturi
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          {/* Retailer + certification proof strip */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            <span className="text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--muted-foreground)' }}>Available on</span>
            {[
              { src: '/assets/images/blinkit.png', alt: 'Blinkit' },
              { src: '/assets/images/Amazon_icon.png', alt: 'Amazon' },
              { src: '/assets/images/flipkart-com-logo.png', alt: 'Flipkart' },
              { src: '/assets/images/instamart.png', alt: 'Swiggy Instamart' },
            ].map((l) => (
              <img key={l.alt} src={`${process.env.NEXT_PUBLIC_BASE_PATH || ''}${l.src}`} alt={l.alt} loading="lazy" className="h-7 w-auto object-contain" />
            ))}
            <span className="hidden sm:block h-5 w-px" style={{ background: 'var(--border)' }} />
            {['FSSAI Certified', 'Lab Tested', 'No Preservatives'].map((c) => (
              <span key={c} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" style={{ border: '1px solid var(--border)', color: 'var(--foreground)', background: 'var(--card)' }}>{c}</span>
            ))}
          </div>
          <WhereToBuyModal open={buyOpen} onClose={() => setBuyOpen(false)} />
        </div>
      </div>
    </section>);

}